import { useId, useState } from 'react';
import { ChevronDown, Fingerprint } from 'lucide-react';
import { useT } from '../../i18n';
import { useRetryWait } from '../ui/useRetryWait';

// ─────────────────────────────────────────────────────────────────────
// «LOGG INN MED PASSKEY», THE SECONDARY DOOR — ONE IMPLEMENTATION.
//
// Extracted from `LoginScreen` so the PLAYER app's entrance
// (`PlayerSignInScreen`) offers the same door the club portal does, rather
// than a second copy of the ceremony's button, its failure sentences and its
// disclosure. The same reasoning that made `AccountCodeSignIn` one component
// for both entrances (#837 review): two copies of a sign-in control drift, and
// the drift is invisible until somebody reads the wrong sentence.
//
// IT RENDERS A FRAGMENT — the disclosure toggle and, when open, the action —
// so `LoginScreen`'s DOM is exactly what it was before the extraction: the two
// elements are still direct children of the screen's own `space-y-3` column.
//
// The CEREMONY is not here. The gate passes `onSignInWithPasskey`, which runs
// `signInWithPasskey()`, adopts the session and decides where the person lands;
// this component turns the FAILURE CODE it gets back into a sentence. A code
// crosses the boundary, never a sentence — the gate has no catalog.
// ─────────────────────────────────────────────────────────────────────

/** Why a passkey sign-in did not happen. A code plus what the sentence needs. */
export type PasskeySignInFailure =
  | {
      code:
        | 'cancelled'
        | 'refused'
        | 'account_disabled'
        | 'account_closed'
        | 'not_configured'
        | 'unreachable';
    }
  /** The server's `Retry-After`, in whole seconds; 0 when it could not be read (#778 Q7). */
  | { code: 'throttled'; retryAfterSeconds: number };

export interface PasskeySignInActionProps {
  /**
   * Run a passkey sign-in. Resolves to `null` on success — the gate takes over
   * and the screen goes away — or to a FAILURE CODE.
   */
  onSignInWithPasskey: () => Promise<PasskeySignInFailure | null>;
}

export function PasskeySignInAction({ onSignInWithPasskey }: PasskeySignInActionProps) {
  const t = useT();
  const passkeyErrorId = useId();
  // Its own error slot, beside its own button: a passkey failure belongs under
  // the passkey action, not in a slot shared with the e-mail code, where a
  // stale sentence would sit beside whichever control is pressed next.
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // A throttled ceremony holds the button for as long as the server said, and
  // the sentence counts down (#778 Q7).
  const wait = useRetryWait<'passkey'>();
  const shownError = passkeyError ?? wait.notice;

  // ── THE SCREEN DOES NOT ENUMERATE ITS DOORS AT REST (#704) ───────
  //
  // «Here are our auth mechanisms, hack us» (Jens, 2026-10-08). A signed-out
  // page that lists every mechanism tells an unauthenticated visitor exactly
  // which exist. BE HONEST ABOUT WHAT THIS BUYS: nothing is hidden from
  // somebody who clicks, and it is not a control — the doors are the same
  // doors. What changes is that the primary door (the e-mail code, #751) is
  // the only thing offered until a person asks for something else.
  //
  // ── A DISCLOSURE THAT ACTUALLY CLOSES (#830 review) ──────────────
  //
  // A real toggle: the label stays, and `aria-expanded` says which way it is.
  const [otherWaysOpen, setOtherWaysOpen] = useState(false);

  /**
   * A failure code becomes a sentence HERE, where the catalog is.
   *
   * `cancelled` is the case worth reading twice: it covers «dismissed the prompt»
   * AND «this device has no passkey for this site», because the browser throws
   * the same error for both — deliberately, so a page cannot learn whether
   * somebody holds a credential for it. The copy is true of both and claims
   * neither.
   */
  const passkeyMessage = (failure: PasskeySignInFailure): string => {
    switch (failure.code) {
      case 'refused':
        return t('auth.passkey.refused');
      case 'account_disabled':
        // NOT «that passkey was not accepted». The credential was accepted; the
        // account behind it is switched off, and telling somebody their passkey
        // failed sends them to re-register a device that is working perfectly.
        return t('errors.account_disabled');
      case 'account_closed':
        // The same reasoning (#1062): the passkey worked, the account is closed.
        return t('errors.account_closed');
      case 'not_configured':
        return t('errors.not_configured');
      case 'unreachable':
        return t('auth.passkey.network');
      default:
        return t('auth.passkey.cancelled');
    }
  };

  /** The quiet text link used for the disclosure. */
  const quietLink =
    'w-full inline-flex items-center justify-center gap-1 text-[12px] text-ink-3 hover:text-ink py-1';

  return (
    <>
      <button
        type="button"
        className={quietLink}
        aria-expanded={otherWaysOpen}
        onClick={() => setOtherWaysOpen((open) => !open)}
      >
        {t('auth.login.otherWays')}
        <ChevronDown
          size={14}
          aria-hidden="true"
          style={{
            transform: otherWaysOpen ? 'rotate(180deg)' : undefined,
            transition: 'transform 120ms',
          }}
        />
      </button>
      {otherWaysOpen && (
        <div className="space-y-3 pt-3 border-t border-line-soft">
          <div>
            <button
              type="button"
              // SECONDARY. It was the primary `btn-lime`; the code is now, and
              // two primary-weight buttons would make the order a decoration
              // rather than a direction.
              className="btn-outline w-full justify-center gap-2 py-2.5"
              disabled={passkeyBusy || wait.waiting('passkey')}
              aria-describedby={shownError ? passkeyErrorId : undefined}
              onClick={() => {
                if (passkeyBusy || wait.waiting('passkey')) return;
                setPasskeyBusy(true);
                setPasskeyError(null);
                wait.release('passkey');
                void onSignInWithPasskey()
                  .then((failure) => {
                    // `null` means signed in — the gate swaps this screen out,
                    // so there is nothing to say and nothing to reset.
                    if (failure === null) return;
                    // NOTHING TO REVEAL: the passkey lives INSIDE this
                    // disclosure, so it is already open, and the e-mail code is
                    // the primary action above it — visible the whole time.
                    // Where the deployment has no Relying Party (#693) every
                    // attempt resolves `not_configured`, and the way in is
                    // already on screen.
                    if (failure.code === 'throttled') {
                      wait.start('passkey', failure.retryAfterSeconds);
                      return;
                    }
                    setPasskeyError(passkeyMessage(failure));
                  })
                  .finally(() => setPasskeyBusy(false));
              }}
            >
              <Fingerprint size={18} aria-hidden="true" />
              {passkeyBusy ? t('auth.passkey.working') : t('auth.passkey.link')}
            </button>
            {shownError && (
              <div
                id={passkeyErrorId}
                role="alert"
                className="mt-3 text-xs text-rose font-medium px-3 py-2 rounded bg-rose/10 border border-rose/20"
              >
                {shownError}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
